import { useState, useCallback } from 'react';
import * as patientService from '../services/patientService';
import { useUIStore } from '../stores/uiStore';
import { notifyApiError } from '../services/core/apiErrorUX';
import { notifyApiSuccess } from '../services/core/apiSuccessUX';

// Hook específico para pacientes
export function usePatients() {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useUIStore();

  const getAllPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await patientService.getPatients();

      if (response.success && response.data) {
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar pacientes',
        });
        return response;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar pacientes',
      });
      return {
        success: false,
        data: null,
        error: 'Error al cargar pacientes',
      };
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  const getPatientById = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await patientService.getPatientById(id);

        if (response.success && response.data) {
          return response;
        } else {
          notifyApiError(addNotification, response, {
            context: 'module',
            fallbackMessage: 'Error al cargar paciente',
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al cargar paciente',
        });
        return {
          success: false,
          data: null,
          error: 'Error al cargar paciente',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const createPatient = useCallback(
    async (patientData: any) => {
      setLoading(true);
      try {
        const response = await patientService.createPatient(patientData);

        if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Paciente Creado', 'Paciente creado correctamente');
          return response;
        } else {
          notifyApiError(addNotification, response, {
            context: 'module',
            fallbackMessage: 'Error al crear paciente',
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al crear paciente',
        });
        return {
          success: false,
          data: null,
          error: 'Error al crear paciente',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const updatePatient = useCallback(
    async (id: string, patientData: any) => {
      setLoading(true);
      try {
        const response = await patientService.updatePatient(id, patientData);

        if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Paciente Actualizado', 'Paciente actualizado correctamente');
          return response;
        } else {
          notifyApiError(addNotification, response, {
            context: 'module',
            fallbackMessage: 'Error al actualizar paciente',
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al actualizar paciente',
        });
        return {
          success: false,
          data: null,
          error: 'Error al actualizar paciente',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const deletePatient = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await patientService.deletePatient(id);

        if (response.success) {
          notifyApiSuccess(addNotification, 'Paciente Eliminado', 'Paciente eliminado correctamente');
          return response;
        } else {
          notifyApiError(addNotification, response, {
            context: 'module',
            fallbackMessage: 'Error al eliminar paciente',
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al eliminar paciente',
        });
        return {
          success: false,
          data: null,
          error: 'Error al eliminar paciente',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  return {
    getAllPatients,
    getPatientById,
    createPatient,
    updatePatient,
    deletePatient,
    loading,
  };
}
